import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IStaffDto, PatchUserDto, ValidationProblemDetails } from '../../../services/api/api-client';
import Style from './userlist.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryFactory } from '../../../services/api';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import { AppCheckboxInput } from 'uikit/inputs';
import { AppDropdownField, AppTextField } from 'uikit/fields';
import { AppInputError } from 'uikit/wrappers';
import { getDoctorStudiesQueryKey, useGetAdminStudiesQuery } from 'src/services/api/api-client/StudyQuery';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import { useGetGeneralRolesQuery } from 'src/services/api/api-client/UserRolesQuery';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { UseModalProps } from 'src/application/hooks/useModal';
import { LocalizedResourceDictionaryKeys } from 'src/application/localisation/i18next';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import { useStaffProfile } from 'src/helpers/hooks/useStudy';
import { getAllUsersQueryKey } from 'src/services/api/api-client/UsersQuery';
import { getDoctorProfileInfoQueryKey } from 'src/services/api/api-client/ProfileQuery';

export const EditUserModalContent: FC<IStaffDto & { modal: UseModalProps }> = (props) => {
  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const client = useQueryClient();
  const { profile } = useStaffProfile();

  const { data: roleList } = useGetGeneralRolesQuery();
  const { data: studies } = useGetAdminStudiesQuery({
    // TODO: This hack must be removed when we will have a dropdown with virtualization
    limit: 99999999,
  });

  // TODO: Rewrite this to hookform
  const [error, setError] = useState<string>();
  const [email, setEmail] = useState<string>(props.email || '');
  const [inProgress, setProgress] = useState<boolean>(false);

  const [selectedStudies, setSelectedStudies] = useState<DropdownOption[] | undefined>([]);
  useEffect(() => {
    setEmail(props.email || '');
    setRoles(props.roles!);
    setSelectedStudies(props.studies!.map((x) => ({ key: x.id, text: `${x.studyTitle} (${x.studyNumber})` })) || []);
  }, [props.email, props.roles, props.studies, props.modal.visible]);

  const [roles, setRoles] = useState(props.roles!);

  // This effect resets validation error
  useEffect(() => {
    setError(undefined);
  }, [email, selectedStudies, roles]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, role: string) => {
      const value = e.target.checked;
      let checkedOptions = roles ? [...roles] : [];
      if (value) {
        checkedOptions.push(role);
      } else {
        checkedOptions = checkedOptions.filter((v) => v !== role);
      }

      setRoles(checkedOptions);
    },
    [roles],
  );

  const closeModalHandler = useCallback(() => {
    props.modal.closeModal();
  }, [props.modal]);

  const onSave = useCallback(async () => {
    try {
      setError('');
      setProgress(true);
      await QueryFactory.UsersQuery.Client.patchUser(
        props.id,
        new PatchUserDto({
          roles,
          studyIds: selectedStudies?.map((x) => +x.key),
          email: email,
        }),
      );

      await Promise.all(
        conditionalConcat(
          [client.invalidateQueries(getAllUsersQueryKey())],
          props.id === profile.id && [
            client.invalidateQueries(getDoctorProfileInfoQueryKey()),
            client.invalidateQueries(getDoctorStudiesQueryKey()),
          ],
        ),
      );

      closeModalHandler();
    } catch (e: any) {
      if (e instanceof ValidationProblemDetails) {
        setError(e.detail ?? undefined);
      } else {
        setError(e?.errors?.Email || t('Admin.UserList.Details.EditModal.Error'));
      }
    } finally {
      setProgress(false);
    }
  }, [props.id, roles, selectedStudies, email, client, profile.id, closeModalHandler, t]);

  const options: DropdownOption[] = useMemo(
    () =>
      studies?.data.map((x) => {
        return { key: x.id, text: `${x.title} (${x.studyNumber})` };
      }) || [],
    [studies?.data],
  );

  return (
    <DialogModal
      visible={props.modal.visible}
      onHide={closeModalHandler}
      title={t('Admin.UserList.Details.EditModal.Header')}
      bodyClassName={Style.modalBody}
      footer={{
        errors: error,
        leftButton: {
          variant: 'button',
          colorSchema: 'secondary',
          text: commonLocalizer('Common_Cancel'),
          onClick: closeModalHandler,
        },
        rightButton: {
          variant: 'button',
          colorSchema: 'primary',
          text: commonLocalizer('Common_Save'),
          onClick: onSave,
          isLoading: inProgress,
          disabled: inProgress,
        },
      }}
    >
      <AppTextField
        labelProps={{
          text: t('Admin.UserList.Details.Login'),
        }}
        value={props.userName}
        disabled={true}
      />

      <AppTextField
        labelProps={{
          text: t('Admin.UserList.Predefined.Email.Label'),
        }}
        placeholder={t('Admin.UserList.Predefined.Email.Placeholder')}
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        disabled={!props.predefinedUser}
      />

      <AppDropdownField
        labelProps={{ text: t('Admin.UserList.Details.EditModal.StudyIds') }}
        isMultiple={true}
        suggestion={true}
        options={options}
        value={selectedStudies}
        onChange={setSelectedStudies}
      />

      <AppInputError>
        {roleList?.map((role, index) => (
          <AppCheckboxInput
            key={index}
            checked={roles?.includes(role.name)}
            label={t(`Admin.UserRoles.GeneralRoles.${role.name}` as LocalizedResourceDictionaryKeys)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e, role.name)}
            disabled={role.name === 'SuperAdmin'}
          />
        ))}
      </AppInputError>
    </DialogModal>
  );
};
